/**
 * 認証番号の控え（検証のサーバだけ・issue #342）。
 *
 * SMS の事業者とつなぐ前は、認証番号の SMS は実際には送られません。検証のサーバでは、
 * 送ったはずの認証番号をこの画面に出し、担当の方がログインする方へお伝えします
 * （実機の確認で、導入先の会社の方にアプリへ入っていただくときなど）。
 *
 * 本番のサーバでは左の項目に出ず、窓口もありません（開いても「ありません」と出る）。
 * 10秒ごとに新しい控えを読み直します。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useApi } from '../api/context.js';
import type { VerificationSmsList } from '../api/types.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { formatTableDateTime } from '../format/datetime.js';
import { formatPhone } from '../format/phone.js';

/** 読み直す間隔（ミリ秒）。 */
export const VERIFICATION_SMS_REFRESH_MS = 10_000;

export function VerificationSmsPage(): ReactNode {
  const api = useApi();
  const [list, setList] = useState<VerificationSmsList | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = (): void => {
      api
        .get<VerificationSmsList>('/admin/verification-sms', { query: { limit: 50 } })
        .then((loaded) => {
          if (cancelled) return;
          setList(loaded);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (!cancelled) setError(loadFailure(caught));
        });
    };
    load();
    const timer = setInterval(load, VERIFICATION_SMS_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, reloadKey]);

  return (
    <article>
      <PageHeader
        title="認証番号の控え"
        description="検証のサーバだけの画面です。SMS はまだ実際には送られないため、ログインしようとしている方の認証番号をここに出します。いちばん上の番号を、その方へお伝えください。番号は10分で使えなくなります。"
        actions={
          <Button variant="secondary" compact onClick={() => setReloadKey((n) => n + 1)}>
            新しくする
          </Button>
        }
      />

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !list ? <LoadingBlock /> : null}

      {list && list.items.length === 0 ? (
        <EmptyBlock label="まだありません。アプリか管理画面で電話番号を入れて「認証番号を送る」を押すと、ここに出ます。" />
      ) : null}

      {list && list.items.length > 0 ? (
        <DataTable label="認証番号の控えの一覧">
          <thead>
            <tr>
              <th scope="col">送った日時</th>
              <th scope="col">お名前</th>
              <th scope="col">電話番号</th>
              <th scope="col">認証番号</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((item) => (
              <tr key={item.id}>
                <td className="ui-cell-date">{formatTableDateTime(item.createdAt)}</td>
                <td>{item.displayName ?? 'メンバーに登録されていない番号（参加の申し込み）'}</td>
                <td>{formatPhone(item.phone)}</td>
                <td>
                  <strong className="verification-sms-code">{item.code ?? item.body}</strong>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      ) : null}
    </article>
  );
}
